import React, { useEffect, useState } from 'react';
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getSession } from "../../utils/session";
import { VIEW_IDS } from "../../constants/ViewIds";

const KPISubHeader = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  const reduxViewIds = useSelector((state) => state.user.viewIds);
  const reduxProjectName = useSelector((state) => state.user.projectName);
  const reduxPid = useSelector((state) => state.user.projectId);
  const reduxIsSuperuser = useSelector((state) => state.user.isSuperuser);
  
  const [viewIds, setViewIds] = useState(reduxViewIds || []);
  const [projectName, setProjectName] = useState(reduxProjectName || '');
  const [pid, setPid] = useState(reduxPid || '');
  const [isSuperuser, setIsSuperuser] = useState(reduxIsSuperuser || false);

  const [executionDate, setExecutionDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (reduxViewIds?.length) {
      setViewIds(reduxViewIds);
    } else {
      const session = getSession();
      if (session?.viewIds?.length) setViewIds(session.viewIds);
    }
    
    // Update project details from Redux
    if (reduxProjectName) {
      setProjectName(reduxProjectName);
    } else {
      const session = getSession();
      if (session?.projectName) setProjectName(session.projectName);
    }
    
    if (reduxPid) {
      setPid(reduxPid);
    } else {
      const session = getSession();
      if (session?.projectId) setPid(session.projectId);
    }
    
    // Update superuser status
    if (reduxIsSuperuser !== undefined) {
      setIsSuperuser(reduxIsSuperuser);
    } else {
      const session = getSession();
      if (session?.isSuperuser !== undefined) setIsSuperuser(session.isSuperuser);
    }
  }, [reduxViewIds, reduxProjectName, reduxPid, reduxIsSuperuser]);

  const canView = (viewId) => viewIds.includes(viewId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://api-project360-insights.haqdarshak.com/api/last-execution-date/');
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setExecutionDate(data.last_execution_date);
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch execution date:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString.replace(' AM', ''));
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  const subNavItems = [
    { path: "/Project-Level", label: "Project Level", viewId: VIEW_IDS.PROJECT_LEVEL },
    { path: "/Project-Scheme-Diversity", label: "Scheme Diversity", viewId: VIEW_IDS.SCHEME_DIVERSITY },
    { path: "/Project-KPI-Comparison", label: "Comparison", viewId: VIEW_IDS.COMPARISON }
  ];

  const visibleSubNavItems = subNavItems.filter(item => canView(item.viewId));

  if (visibleSubNavItems.length === 0) return null;

  return (
    <div className={`fixed top-[64px] left-0 right-0 z-40 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200' 
        : 'bg-gradient-to-r from-gray-50 to-white border-b border-gray-100'
    }`}>
      <div className="px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row justify-between items-center py-2 sm:py-3 gap-2">
          
          {/* Left Section - Project Info with Superuser Badge (Desktop) */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center space-x-3 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-1.5 rounded-full border border-blue-100">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                  {projectName || 'No Project'}
                </span>
              </div>
              <div className="w-px h-5 bg-blue-200"></div>
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                <span className="text-sm font-mono font-semibold text-purple-700">
                  {pid || 'No PID'}
                </span>
              </div>
              {/* Superuser Badge - Desktop */}
              {isSuperuser && (
                <>
                  <div className="w-px h-5 bg-blue-200"></div>
                  <div className="flex items-center space-x-1">
                    <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    <span className="text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-transparent bg-clip-text">
                      SUPERUSER
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Navigation Tabs - Centered */}
          <div className="flex items-center space-x-1 bg-gray-100/50 rounded-lg p-1">
            {visibleSubNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  location.pathname === item.path
                    ? "bg-white text-indigo-600 shadow-sm ring-1 ring-indigo-200"
                    : "text-gray-600 hover:text-indigo-600 hover:bg-white/50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right Section - Project Info (Mobile/Tablet) + Last Update */}
          <div className="flex items-center space-x-3">
            {/* Mobile/Tablet Project Info */}
            <div className="flex lg:hidden items-center space-x-2 bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-1 rounded-full border border-blue-100">
              <div className="flex items-center space-x-1">
                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-xs font-medium text-gray-700 truncate max-w-[80px]">
                  {projectName || 'No Project'}
                </span>
              </div>
              <div className="w-px h-4 bg-blue-200"></div>
              <div className="flex items-center space-x-1">
                <svg className="w-3.5 h-3.5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                <span className="text-xs font-mono font-semibold text-purple-700">
                  {pid || 'No PID'}
                </span>
              </div>
              {/* Superuser Badge - Mobile/Tablet */}
              {isSuperuser && (
                <>
                  <div className="w-px h-4 bg-blue-200"></div>
                  <div className="flex items-center space-x-1">
                    <svg className="w-3 h-3 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    <span className="text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-transparent bg-clip-text">
                      SUPER
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Last Update Badge */}
            <div>
              {isLoading ? (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 rounded-full">
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-500 border-t-transparent"></div>
                  <span className="text-xs text-gray-600">Updating...</span>
                </div>
              ) : error ? (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-red-50 rounded-full">
                  <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs text-red-600">Update failed</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full border border-green-100">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                    </div>
                    <svg className="w-3.5 h-3.5 text-green-600 relative" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-xs text-gray-700">
                    Last updated: <span className="font-semibold text-gray-900">{formatDate(executionDate)}</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPISubHeader;